import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { ContentLayout } from '@/bundle/Layouts/ContentLayout/ContentLayout';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { useInterval } from '@/hooks/useInterval';
import { WIRE_STATUS_DB } from '@/const/wire';
import {
  isPaymentDetailsProvidedWireStatus,
  isPendingWireStatus,
  isRecipientAssignedWireStatus,
  isRecipientChainProxy,
  isRecipientProxy,
} from '@/helpers/wireHelpers';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  changeWireStatus,
  ChangeWireStatusType,
  createReplacementWire,
  loadOutboundWireDetails,
  loadPaymentDetails,
  loadSourceWirePaymentDetails,
  registerBillingEvent,
} from '@/bundle/_OrgAdmin/pages/OutboundWire/api/api';
import { PageContent } from '@/components/Page/PageContent/PageContent';
import { PageColumn } from '@/components/Page/PageColumn/PageColumn';
import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { Drawer } from '@/components/Drawer/Drawer';
import { OutboundWirePaymentDetails } from '@/bundle/_OrgAdmin/pages/OutboundWire/ui/OutboundWirePaymentDetails/OutboundWirePaymentDetails';
import { OutboundWireGeneralDetails } from '@/bundle/_OrgAdmin/pages/OutboundWire/ui/OutboundWireGeneralDetails/OutboundWireGeneralDetails';
import { getHeaderActionMessage } from './const/const';
import { ReplacementWireDataType } from '@/api/v1/organization/wires/createReplacementWireApi';
import { getAmplitudePaymentDetailsProvider } from '@/services/amplitudeService/amplitudeHelpers';
import { OutboundWireHeaderActions } from './ui/OutboundWireHeaderActions/OutboundWireHeaderActions';
import { SourceWirePaymentDetails } from '@/bundle/shared/components/SourceWirePaymentDetails/SourceWirePaymentDetails';
import { ExecutorActionsWidget } from './ui/ExecutorActionsWidget/ExecutorActionsWidget';
import { RecipientActionsWidget } from './ui/RecipientActionsWidget/RecipientActionsWidget';
import { ViewPaymentDetails } from './ui/ViewPaymentDetails/ViewPaymentDetails';
import { OutboundWireUserDetails } from './ui/OutboundWireUserDetails/OutboundWireUserDetails';
import { RedirectButton } from '@/components/RedirectButton/RedirectButton';
import { getShowPaymentDetails } from '@/helpers/paymentDetailsHelpers';
import { getOutboundWireDetailsPath } from './path/path';
import { useGetCurrencyOptions } from '@/bundle/shared/hooks/useGetCurrencyOptions';
import { OrganizationSubscriptionStatusBanner } from '@/bundle/shared/components/banner/OrganizationSubscriptionStatusBanner/OrganizationSubscriptionStatusBanner';
import { useSubscriptionStatus } from '@/bundle/shared/hooks/useSubscriptionStatus';
import { RefetchUserWrapper } from '@/bundle/Routes/RefetchUserRoute';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { getTransactionDetailsPath } from '../TransactionDetailsPage/path/path';
import { CreateReplacementWireForm } from './ui/CreateReplacementWireForm/CreateReplacementWireForm';
import { useStatefulNavigation } from '@/hooks/useStatefulNavigation';

export const OutboundWireDetailsPage = () => {
  const queryClient = useQueryClient();
  const { transactionId, id } = useParams<{ transactionId: string; id: string }>();
  const { navigateBack } = useStatefulNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isInactiveSubscription } = useSubscriptionStatus();

  const [isEdit, setIsEdit] = useState(false);
  const [isShowRecipientEditForm, setIsShowRecipientEditForm] = useState(false);
  const [isShowExecutorAssignForm, setIsShowExecutorAssignForm] = useState(false);
  const [showExecutorEditFormIds, setShowExecutorEditFormIds] = useState<Record<string, boolean>>({});

  const [isOpenCancelWireModal, setIsOpenCancelWireModal] = useState(false);
  const [isOpenExecuteWireModal, setIsOpenExecuteWireModal] = useState(false);
  const [isOpenCreateWireForm, setIsOpenCreateWireForm] = useState(false);

  const { data: outboundWireData, isPending: isPendingOutboundWire } = useQuery({
    queryKey: ['load_outbound_wire', id],
    queryFn: () => loadOutboundWireDetails(id),
  });

  const refetchOutboundWire = () => {
    queryClient.invalidateQueries({ queryKey: ['load_outbound_wire', id] });
  };

  const outboundWire = outboundWireData?.body;
  const wireStatus = outboundWire?.status;
  const isRecipientAssignStatus = isRecipientAssignedWireStatus(wireStatus);
  const isPaymentDetailsProvidedStatus = isPaymentDetailsProvidedWireStatus(wireStatus);

  const isPaymentDetailsExist = !!outboundWire?.is_payment_details_exist;
  const isBillingEvent = !!outboundWire?.view_payment_details_event_register;
  const hasSourceWirePaymentDetails = !!(outboundWire?.payment_details_source_wire?.id && isRecipientAssignStatus);
  const isProvidedPaymentDetails = isPaymentDetailsExist && isBillingEvent;
  const wireId = isProvidedPaymentDetails ? id : outboundWire?.payment_details_source_wire?.id;
  const hasRedirectButton = location?.state?.transactionId && location?.state?.id;
  const showBanner = isInactiveSubscription && isPaymentDetailsProvidedStatus;

  // Note: we load different payment details based on source wire:
  // a) if source wire exists and waiting for confirmation - load source wire payment details
  // b) if payment details has been already provided - load default payment details
  const { data: paymentDetailsData, isFetching: isFetchingPaymentDetails } = useQuery({
    queryKey: ['load_payment_details', wireId],
    queryFn: () => loadPaymentDetails(wireId),
    enabled: isProvidedPaymentDetails,
  });

  const { data: sourceWirePaymentDetailsData } = useQuery({
    queryKey: ['load_source_wire_payment_details', id],
    queryFn: () => loadSourceWirePaymentDetails(id),
    enabled: hasSourceWirePaymentDetails,
  });

  const {
    mutate: mutateRegisterBillingEvent,
    data: billingEventData,
    isPending: isPendingBillingEvent,
  } = useMutation({
    mutationKey: ['register_billing_event'],
    mutationFn: () => {
      if (isPendingBillingEvent) return;

      return registerBillingEvent(id);
    },
    onSuccess(response) {
      if (response?.error) return;

      refetchOutboundWire();
    },
  });

  const { mutate: mutateChangeWireStatus, isPending: isPendingChangeWireStatus } = useMutation({
    mutationKey: ['change_wire_status'],
    mutationFn: (status: ChangeWireStatusType) => {
      return changeWireStatus(id, status);
    },
    onSuccess(response) {
      if (response?.error) {
        return showSnackbarResponseError(response?.error);
      }

      queryClient.setQueryData(['load_outbound_wire', id], response);
    },
  });

  const isShowPaymentDetails = getShowPaymentDetails(
    !!paymentDetailsData?.body,
    outboundWire,
    !!billingEventData?.body
  );

  const registerShowPaymentDetailsEvent = () => {
    amplitudeService.logEvent(AMPLITUDE_EVENTS.ViewPaymentDetails);
    mutateRegisterBillingEvent();
  };

  const {
    data: createChildWireData,
    mutate: mutateCreateChildWire,
    isPending: isPendingCreateChildWire,
    reset: resetCreateChildWire,
  } = useMutation({
    mutationKey: ['create_child_wire'],
    mutationFn: (payload: ReplacementWireDataType) => createReplacementWire(id, payload),
    onSuccess(response) {
      if (response?.error) return;

      const isProxy = isRecipientProxy(outboundWire);
      const isChainProxy = isRecipientChainProxy(outboundWire);
      const provider = getAmplitudePaymentDetailsProvider(isChainProxy, isProxy);

      amplitudeService.logEvent(AMPLITUDE_EVENTS.CreateChildOutboundWireSuccess, { provider });

      setIsOpenCreateWireForm(false);
      navigate(getOutboundWireDetailsPath(transactionId, response?.body?.id));
      refetchOutboundWire();
    },
  });

  const { currencyOptions } = useGetCurrencyOptions(!!(isEdit || isOpenCreateWireForm));

  const headerActionMessage = getHeaderActionMessage(outboundWire);
  const isPendingStatus = isPendingWireStatus(wireStatus);

  // polling wire details logic
  const { cancelInterval, isIntervalRunning } = useInterval(() => {
    if (isPendingStatus) {
      refetchOutboundWire();
    }
  });

  //handlers block
  const navigateToTransactionDetails = () => {
    navigateBack(getTransactionDetailsPath(transactionId));
  };

  const openEditMode = () => {
    amplitudeService.logEvent(AMPLITUDE_EVENTS.EditGeneralInfoOutboundWireRedirect);
    setIsEdit(true);
  };

  const confirmCancelWire = () => {
    mutateChangeWireStatus(WIRE_STATUS_DB.CANCELLED);
    setIsOpenCancelWireModal(false);

    if (isShowExecutorAssignForm) {
      setIsShowExecutorAssignForm(false);
    }

    amplitudeService.logEvent(AMPLITUDE_EVENTS.CancelOutboundWireSuccess);
  };

  const confirmExecuteWire = () => {
    amplitudeService.logEvent(AMPLITUDE_EVENTS.ExecuteWireSuccess);
    setIsOpenExecuteWireModal(false);

    mutateChangeWireStatus(WIRE_STATUS_DB.EXECUTED);
  };

  const redirectToWire = () => {
    navigate(getOutboundWireDetailsPath(location.state?.transactionId, location.state?.id));
  };

  // Reset edit mode when switching between details pages
  useEffect(() => setIsEdit(false), [id, transactionId]);

  // Cancel polling logic
  useEffect(() => {
    const shouldCancelInterval = !isPendingStatus && isIntervalRunning;

    if (shouldCancelInterval) {
      cancelInterval();
    }
  }, [wireStatus, cancelInterval, isIntervalRunning, isPendingStatus]);

  const closeReplacementWireModal = () => {
    resetCreateChildWire();
    setIsOpenCreateWireForm(false);
  };

  const openEditRecipientForm = () => {
    amplitudeService.logEvent(AMPLITUDE_EVENTS.EditRecipientInfoRedirect);
    setIsShowRecipientEditForm(true);
  };

  const openEditExecutorForm = (id: string) => {
    setIsShowExecutorAssignForm(false);
    setShowExecutorEditFormIds({
      [id]: true,
    });

    amplitudeService.logEvent(AMPLITUDE_EVENTS.EditExecutorInfoRedirect);
  };

  const closeEditExecutorForm = (id: string) => {
    setShowExecutorEditFormIds({
      [id]: false,
    });
  };

  const onUpdateWireDetails = () => {
    setIsEdit(false);
  };

  const openCancelWireDialog = () => {
    setIsOpenCancelWireModal(true);
    amplitudeService.logEvent(AMPLITUDE_EVENTS.CancelOutboundWireStartRedirect);
  };

  const openExecuteWireDialog = () => {
    setIsOpenExecuteWireModal(true);
    amplitudeService.logEvent(AMPLITUDE_EVENTS.ExecuteWireRedirect);
  };

  const openAssignExecutorForm = () => {
    setIsShowExecutorAssignForm(true);
    setShowExecutorEditFormIds({});
    amplitudeService.logEvent(AMPLITUDE_EVENTS.AssignExecutorRedirect);
  };

  const openCreateReplacementWireForm = () => {
    setIsOpenCreateWireForm(true);
    amplitudeService.logEvent(AMPLITUDE_EVENTS.CreateChildOutboundWireRedirect);
  };

  const paymentDetails = paymentDetailsData?.body;
  const sourceWirePaymentDetails = sourceWirePaymentDetailsData?.body;

  return (
    <ContentLayout
      title='Wire details'
      breadcrumbs='Transactions > Details > Wire Details'
      headerAction={
        !!outboundWire && (
          <OutboundWireHeaderActions
            wire={outboundWire}
            isLoadingWire={isPendingOutboundWire}
            viewPaymentDetails={isShowPaymentDetails}
            isShowExecutorAssignForm={isShowExecutorAssignForm}
            onOpenCancelWireDialog={openCancelWireDialog}
            onOpenExecuteWireDialog={openExecuteWireDialog}
            onAssignExecutorRedirect={openAssignExecutorForm}
            onOpenCreateReplacementWireForm={openCreateReplacementWireForm}
          />
        )
      }
      headerActionMessage={headerActionMessage}
      backButton='Back to Transaction'
      onBack={navigateToTransactionDetails}
      apiError={outboundWireData?.error}
      banner={showBanner && <OrganizationSubscriptionStatusBanner />}
    >
      <PageBody>
        <PageContent>
          <PageColumn>
            <OutboundWireGeneralDetails
              wire={outboundWire}
              wireApiError={outboundWireData?.error}
              currencyOptions={currencyOptions}
              isEdit={isEdit}
              onUpdateWireDetails={onUpdateWireDetails}
              onEditWireDetails={openEditMode}
            />

            <OutboundWireUserDetails
              wire={outboundWire}
              showExecutorEditFormIds={showExecutorEditFormIds}
              isShowRecipientEditForm={isShowRecipientEditForm}
              onEditRecipientForm={openEditRecipientForm}
              onEditExecutorForm={openEditExecutorForm}
            />
          </PageColumn>
          <PageColumn>
            <ExecutorActionsWidget
              wire={outboundWire}
              onRefetchWire={refetchOutboundWire}
              isShowAssignForm={isShowExecutorAssignForm}
              showExecutorEditFormIds={showExecutorEditFormIds}
              onCloseAssignForm={() => setIsShowExecutorAssignForm(false)}
              onCloseEditForm={closeEditExecutorForm}
            />

            <RecipientActionsWidget
              wire={outboundWire}
              onRefetchWire={refetchOutboundWire}
              isShowEditForm={isShowRecipientEditForm}
              onCloseEditForm={() => setIsShowRecipientEditForm(false)}
            />

            {hasSourceWirePaymentDetails && sourceWirePaymentDetails && (
              <SourceWirePaymentDetails
                paymentDetails={sourceWirePaymentDetails}
                notification='Waiting for the Recipient to confirm their wire info.'
                wire={outboundWire}
              />
            )}

            {isPaymentDetailsExist && isShowPaymentDetails && (
              <OutboundWirePaymentDetails
                wire={outboundWire}
                paymentDetails={paymentDetails}
                isLoading={isFetchingPaymentDetails}
                apiError={paymentDetailsData?.error}
              />
            )}

            {isPaymentDetailsExist && !isBillingEvent && (
              <RefetchUserWrapper>
                <ViewPaymentDetails
                  wire={outboundWire}
                  isShowPaymentDetails={isShowPaymentDetails}
                  billingEventApiError={billingEventData?.error}
                  isLoadingBillingEvent={isPendingBillingEvent}
                  onViewPaymentDetails={registerShowPaymentDetailsEvent}
                />
              </RefetchUserWrapper>
            )}
          </PageColumn>
        </PageContent>
      </PageBody>

      <ConfirmModal
        isOpen={isOpenCancelWireModal}
        isLoading={isPendingChangeWireStatus}
        header='Cancel Wire'
        body='You will not be able to view this wire once you cancel.'
        onClose={() => setIsOpenCancelWireModal(false)}
        onConfirm={confirmCancelWire}
      />

      <ConfirmModal
        isOpen={isOpenExecuteWireModal}
        isLoading={isPendingChangeWireStatus}
        header='Mark as Executed'
        body='You are marking this wire as successfully executed. The recipient of this wire will be notified that the wire has been sent.'
        onClose={() => setIsOpenExecuteWireModal(false)}
        onConfirm={confirmExecuteWire}
      />
      <Drawer
        isOpen={isOpenCreateWireForm}
        header='Create Replacement Wire'
        subHeader='You are creating a replacement wire. This will allow you to update the wire info for the assigned recipient. This collection will not be billed and you cannot change the recipient of this wire.'
        onClose={closeReplacementWireModal}
      >
        <CreateReplacementWireForm
          wire={outboundWire}
          currencyOptions={currencyOptions}
          onCreate={mutateCreateChildWire}
          isLoading={isPendingCreateChildWire}
          onClose={closeReplacementWireModal}
          error={createChildWireData?.error}
        />
      </Drawer>
      {hasRedirectButton && <RedirectButton onNavigate={redirectToWire} />}
    </ContentLayout>
  );
};
